import {useIntl} from 'react-intl';
import {Box, BoxProps, Center, Flex, FlexProps, Image, Text, useBreakpointValue} from '@chakra-ui/react';
import {ReactNode} from 'react';

type MatchProps = {
  match: MatchT;
  underScoreNode?: ReactNode;
} & FlexProps;

export function Match({match, ...props}: MatchProps) {
  const homeTeam = useBreakpointValue([match.home_team.code ?? match.home_team.name, match.home_team.name]);
  const awayTeam = useBreakpointValue([match.away_team.code ?? match.away_team.name, match.away_team.name]);

  return (
    <Flex justify="space-between" gap={[5, null, 10]} w="full" {...props}>
      <Team team={homeTeam!} logo={match.home_team.logo?.original} type="home" />

      {match.type === 'football' && (
        <Center flexDir="column">
          <Score homeGoals={match.home_goals} awayGoals={match.away_goals} />
        </Center>
      )}

      <Team team={awayTeam!} logo={match.away_team.logo?.original} type="away" />
    </Flex>
  );
}

interface TeamProps extends BoxProps {
  team: string;
  type: 'home' | 'away';
  logo: string | undefined;
}

function Team({team, type, logo, ...props}: TeamProps) {
  return (
    <Flex
      flex="1"
      flexDir={['column', null, type === 'home' ? 'row' : 'row-reverse']}
      justify="center"
      align="center"
      gap={[1, null, 2]}
      {...props}
    >
      <Image src={logo} alt={team} w="10" h="10" />

      <Text
        flex={['initial', null, 1]}
        noOfLines={[1, null, 3]}
        fontSize="sm"
        textAlign={['center', null, type === 'home' ? 'start' : 'end']}
        title={team}
      >
        {team}
      </Text>
    </Flex>
  );
}

function Score({homeGoals = 0, awayGoals = 0}: {awayGoals: number; homeGoals: number}) {
  const format = useIntl().formatNumber;

  return (
    <Box display="flex" alignSelf="center">
      <Center
        w={['8', '10']}
        h={['8', '10']}
        borderWidth="1px"
        borderEndWidth="0"
        borderColor="black"
        fontWeight="bold"
        rounded="sm"
        roundedEnd="0"
        bgColor={homeGoals > awayGoals ? 'green.400' : 'white'}
      >
        {format(homeGoals)}
      </Center>

      <Center
        w={['8', '10']}
        h={['8', '10']}
        fontWeight="bold"
        borderWidth="1px"
        borderColor="black"
        rounded="sm"
        roundedStart="0"
        bgColor={awayGoals > homeGoals ? 'green.400' : 'white'}
      >
        {format(awayGoals)}
      </Center>
    </Box>
  );
}
